import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MailCheck, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
const TOKEN_KEY = "auth_token";

function cleanBearer(t?: string | null) {
  let raw = (t || "").trim();
  if (raw.toLowerCase().startsWith("bearer ")) raw = raw.slice(7).trim();
  return raw;
}

export default function VerifyInitialCode() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    const c = code.replace(/\D/g, "").slice(0, 6);
    if (!/^[0-9]{6}$/.test(c)) {
      toast({ title: "Code invalide", description: "Entrez 6 chiffres.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const token = cleanBearer(localStorage.getItem(TOKEN_KEY));
      const res = await fetch(`${API_BASE}/auth/verify-initial-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: c }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Erreur ${res.status}`);

      localStorage.setItem("must_verify_code", "0");
      toast({ title: "Code vérifié ✅", description: "Vous pouvez maintenant changer votre mot de passe." });
      navigate("/first-login-change-password");
    } catch (err: any) {
      toast({ title: "Échec", description: err?.message || "Erreur inconnue", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setSending(true);
    try {
      const token = cleanBearer(localStorage.getItem(TOKEN_KEY));
      const res = await fetch(`${API_BASE}/auth/request-initial-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Erreur ${res.status}`);
      toast({ title: "Code renvoyé", description: "Vérifiez votre boîte e-mail." });
    } catch (err: any) {
      toast({ title: "Échec de l'envoi", description: err?.message || "Erreur inconnue", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      {/* Background flou */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: "ur[](https://images.unsplash.com/photo-1557682250-33bd709cbe85?auto=format&fit=crop&w=1920&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(10px)",
          transform: "scale(1.08)",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-background/70 via-background/50 to-background/70" />

      {/* Carte */}
      <div className="relative w-full max-w-md animate-in fade-in-0 zoom-in-95 duration-300">
        <div className="rounded-3xl shadow-2xl backdrop-blur-xl border border-white/20 bg-white/30 dark:bg-black/30">
          <div className="p-6 sm:p-7 space-y-4">
            <div className="flex items-center gap-2">
              <MailCheck className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-semibold">Vérification par code</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Un code à 6 chiffres vous a été envoyé par e-mail. Saisissez-le ci-dessous pour continuer.
            </p>

            <form onSubmit={verify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code reçu</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  title="Entrez 6 chiffres"
                  placeholder="000000"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  className="bg-background/60"
                />
              </div>

              <div className="flex items-center justify-between gap-2">
                <Button type="submit" className="flex-1 h-11" disabled={busy}>
                  {busy ? "Vérification…" : "Vérifier"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2 h-11"
                  onClick={resend}
                  disabled={sending}
                >
                  <RefreshCw className="h-4 w-4" />
                  Renvoyer
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
